import React, { useEffect, useState } from 'react';
import { ChevronDownIcon, ArrowUpIcon, ArrowDownIcon, InfoIcon, AlertCircleIcon, CheckCircleIcon, RefreshCwIcon, DollarSignIcon, CreditCardIcon, UsersIcon, BuildingIcon } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, PieChart, Pie, Cell } from 'recharts';
export function Dashboard() {
  const [timeFrame, setTimeFrame] = useState<'today' | 'month' | 'year'>('today');
  const [showTimeFrameDropdown, setShowTimeFrameDropdown] = useState(false);
  const [filteredKpiData, setFilteredKpiData] = useState<any[]>([]);
  const [kpiFilters, setKpiFilters] = useState<{
    [key: string]: 'all' | 'success' | 'failed';
  }>({
    'P2P Transfers': 'all',
    'P2M Payments': 'all',
    'QRC Payments': 'all',
    'RTP Payments': 'all',
    'Bulk Payments': 'all',
    'Bulk Receiving': 'all',
    'ALIAS Registered': 'all'
  });
  const [lastUpdated, setLastUpdated] = useState(new Date());
  // Bank information - hardcoded for this bank portal
  const bankInfo = {
    code: 'AMHRETAA',
    name: 'Amhara Bank S.C.'
  };
  // System status data
  const systemStatus = [{
    name: 'Core Banking',
    status: 'operational'
  }, {
    name: 'Payment Gateway',
    status: 'operational'
  }, {
    name: 'Mobile Banking',
    status: 'operational'
  }, {
    name: 'ATM Network',
    status: 'degraded'
  }];
  // Transaction distribution data
  const transactionDistribution = [{
    name: 'P2P',
    value: 45
  }, {
    name: 'P2M',
    value: 30
  }, {
    name: 'QRC',
    value: 15
  }, {
    name: 'RTP',
    value: 10
  }];
  const COLORS = ['#F58220', '#1A1F36', '#4C9AFF', '#36B37E'];
  // Base KPI data
  const getKpiData = () => {
    // Generate data based on timeFrame
    const multiplier = timeFrame === 'today' ? 1 : timeFrame === 'month' ? 30 : 365;
    return [{
      title: 'P2P Transfers',
      count: {
        all: Math.round(1243 * multiplier),
        success: Math.round(1105 * multiplier),
        failed: Math.round(138 * multiplier)
      },
      value: {
        all: Math.round(4528900 * multiplier),
        success: Math.round(4025000 * multiplier),
        failed: Math.round(503900 * multiplier)
      },
      trend: 'up',
      percent: 12,
      icon: <CreditCardIcon size={20} className="text-blue-600" data-id="element-592" />
    }, {
      title: 'P2M Payments',
      count: {
        all: Math.round(965 * multiplier),
        success: Math.round(920 * multiplier),
        failed: Math.round(45 * multiplier)
      },
      value: {
        all: Math.round(3256800 * multiplier),
        success: Math.round(3100000 * multiplier),
        failed: Math.round(156800 * multiplier)
      },
      trend: 'up',
      percent: 18,
      icon: <CreditCardIcon size={20} className="text-indigo-600" data-id="element-593" />
    }, {
      title: 'QRC Payments',
      count: {
        all: Math.round(892 * multiplier),
        success: Math.round(845 * multiplier),
        failed: Math.round(47 * multiplier)
      },
      value: {
        all: Math.round(2134500 * multiplier),
        success: Math.round(2020300 * multiplier),
        failed: Math.round(114200 * multiplier)
      },
      trend: 'up',
      percent: 8,
      icon: <DollarSignIcon size={20} className="text-green-600" data-id="element-594" />
    }, {
      title: 'RTP Payments',
      count: {
        all: Math.round(567 * multiplier),
        success: Math.round(520 * multiplier),
        failed: Math.round(47 * multiplier)
      },
      value: {
        all: Math.round(1876300 * multiplier),
        success: Math.round(1750000 * multiplier),
        failed: Math.round(126300 * multiplier)
      },
      trend: 'down',
      percent: 3,
      icon: <CreditCardIcon size={20} className="text-purple-600" data-id="element-595" />
    }, {
      title: 'Bulk Payments',
      count: {
        all: Math.round(124 * multiplier),
        success: Math.round(118 * multiplier),
        failed: Math.round(6 * multiplier)
      },
      value: {
        all: Math.round(5432100 * multiplier),
        success: Math.round(5300000 * multiplier),
        failed: Math.round(132100 * multiplier)
      },
      trend: 'up',
      percent: 15,
      icon: <BuildingIcon size={20} className="text-orange-600" data-id="element-596" />
    }, {
      title: 'Bulk Receiving',
      count: {
        all: Math.round(98 * multiplier),
        success: Math.round(92 * multiplier),
        failed: Math.round(6 * multiplier)
      },
      value: {
        all: Math.round(4321000 * multiplier),
        success: Math.round(4150000 * multiplier),
        failed: Math.round(171000 * multiplier)
      },
      trend: 'down',
      percent: 5,
      icon: <BuildingIcon size={20} className="text-teal-600" data-id="element-597" />
    }, {
      title: 'ALIAS Registered',
      count: {
        all: Math.round(432 * multiplier),
        success: Math.round(432 * multiplier),
        failed: 0
      },
      value: {
        all: 0,
        success: 0,
        failed: 0
      },
      trend: 'up',
      percent: 23,
      icon: <UsersIcon size={20} className="text-indigo-600" data-id="element-598" />
    }];
  };
  // Sample data for charts - today
  const transactionDataToday = [{
    name: '00:00',
    p2p: 400,
    p2m: 320,
    qrc: 240,
    rtp: 180
  }, {
    name: '04:00',
    p2p: 300,
    p2m: 250,
    qrc: 139,
    rtp: 280
  }, {
    name: '08:00',
    p2p: 200,
    p2m: 310,
    qrc: 980,
    rtp: 290
  }, {
    name: '12:00',
    p2p: 278,
    p2m: 290,
    qrc: 390,
    rtp: 200
  }, {
    name: '16:00',
    p2p: 189,
    p2m: 210,
    qrc: 480,
    rtp: 281
  }, {
    name: '20:00',
    p2p: 239,
    p2m: 270,
    qrc: 380,
    rtp: 250
  }];
  // Sample data for charts - month
  const transactionDataMonth = [{
    name: 'Week 1',
    p2p: 1200,
    p2m: 1000,
    qrc: 840,
    rtp: 680
  }, {
    name: 'Week 2',
    p2p: 1500,
    p2m: 1250,
    qrc: 939,
    rtp: 880
  }, {
    name: 'Week 3',
    p2p: 1300,
    p2m: 1150,
    qrc: 1080,
    rtp: 790
  }, {
    name: 'Week 4',
    p2p: 1400,
    p2m: 1300,
    qrc: 1190,
    rtp: 900
  }];
  // Sample data for charts - year
  const transactionDataYear = [{
    name: 'Jan',
    p2p: 4200,
    p2m: 3800,
    qrc: 3240,
    rtp: 2180
  }, {
    name: 'Feb',
    p2p: 3800,
    p2m: 3500,
    qrc: 2939,
    rtp: 2380
  }, {
    name: 'Mar',
    p2p: 4500,
    p2m: 4100,
    qrc: 3580,
    rtp: 2590
  }, {
    name: 'Apr',
    p2p: 5200,
    p2m: 4800,
    qrc: 3890,
    rtp: 3200
  }, {
    name: 'May',
    p2p: 4800,
    p2m: 4400,
    qrc: 4080,
    rtp: 3281
  }, {
    name: 'Jun',
    p2p: 5500,
    p2m: 5100,
    qrc: 4380,
    rtp: 3650
  }];
  // Sample data for value charts - today
  const valueDataToday = [{
    name: '00:00',
    p2p: 2400,
    p2m: 1800,
    qrc: 1240,
    rtp: 1180
  }, {
    name: '04:00',
    p2p: 1300,
    p2m: 1500,
    qrc: 1139,
    rtp: 1280
  }, {
    name: '08:00',
    p2p: 1200,
    p2m: 1400,
    qrc: 1980,
    rtp: 1290
  }, {
    name: '12:00',
    p2p: 1278,
    p2m: 1350,
    qrc: 1390,
    rtp: 1200
  }, {
    name: '16:00',
    p2p: 1189,
    p2m: 1250,
    qrc: 1480,
    rtp: 1281
  }, {
    name: '20:00',
    p2p: 1239,
    p2m: 1300,
    qrc: 1380,
    rtp: 1250
  }];
  // Sample data for value charts - month
  const valueDataMonth = [{
    name: 'Week 1',
    p2p: 12400,
    p2m: 10800,
    qrc: 8240,
    rtp: 6180
  }, {
    name: 'Week 2',
    p2p: 15300,
    p2m: 13500,
    qrc: 9139,
    rtp: 8280
  }, {
    name: 'Week 3',
    p2p: 13200,
    p2m: 12400,
    qrc: 10980,
    rtp: 7290
  }, {
    name: 'Week 4',
    p2p: 14278,
    p2m: 13500,
    qrc: 11390,
    rtp: 9200
  }];
  // Sample data for value charts - year
  const valueDataYear = [{
    name: 'Jan',
    p2p: 42400,
    p2m: 38000,
    qrc: 32240,
    rtp: 21180
  }, {
    name: 'Feb',
    p2p: 38300,
    p2m: 35000,
    qrc: 29139,
    rtp: 23280
  }, {
    name: 'Mar',
    p2p: 45200,
    p2m: 41000,
    qrc: 35980,
    rtp: 25290
  }, {
    name: 'Apr',
    p2p: 52278,
    p2m: 48000,
    qrc: 38390,
    rtp: 32200
  }, {
    name: 'May',
    p2p: 48189,
    p2m: 44000,
    qrc: 40480,
    rtp: 32810
  }, {
    name: 'Jun',
    p2p: 55239,
    p2m: 51000,
    qrc: 43380,
    rtp: 36250
  }];
  // Sample data for recent activity
  const getRecentActivity = () => {
    return [{
      id: 1,
      dateTime: '2023-05-10 14:30:22',
      sender: bankInfo.code,
      receiver: 'CBETETAA',
      amount: 5000,
      txnType: 'P2P',
      status: 'Success'
    }, {
      id: 2,
      dateTime: '2023-05-10 14:28:15',
      sender: bankInfo.code,
      receiver: 'DASHETAA',
      amount: 3500,
      txnType: 'P2M',
      status: 'Failed'
    }, {
      id: 3,
      dateTime: '2023-05-10 14:25:47',
      sender: 'BUNAETAA',
      receiver: bankInfo.code,
      amount: 2800,
      txnType: 'P2P',
      status: 'Success'
    }, {
      id: 4,
      dateTime: '2023-05-10 14:20:33',
      sender: 'NIBIETAA',
      receiver: bankInfo.code,
      amount: 4200,
      txnType: 'P2M',
      status: 'Success'
    }, {
      id: 5,
      dateTime: '2023-05-10 14:15:19',
      sender: bankInfo.code,
      receiver: 'WEGAETAA',
      amount: 1500,
      txnType: 'P2P',
      status: 'Failed'
    }];
  };
  // Get the appropriate transaction data based on timeframe
  const getTransactionData = () => {
    let data;
    switch (timeFrame) {
      case 'month':
        data = transactionDataMonth;
        break;
      case 'year':
        data = transactionDataYear;
        break;
      default:
        data = transactionDataToday;
    }
    return data;
  };
  // Get the appropriate value data based on timeframe
  const getValueData = () => {
    let data;
    switch (timeFrame) {
      case 'month':
        data = valueDataMonth;
        break;
      case 'year':
        data = valueDataYear;
        break;
      default:
        data = valueDataToday;
    }
    return data;
  };
  // Update KPI filters
  const handleKpiFilterChange = (title: string, filter: 'all' | 'success' | 'failed') => {
    setKpiFilters(prev => ({
      ...prev,
      [title]: filter
    }));
  };
  const handleRefresh = () => {
    setLastUpdated(new Date());
    // Here you would typically fetch fresh data
  };
  // Update filtered KPI data when filters change or when timeFrame changes
  useEffect(() => {
    const kpiData = getKpiData();
    const filtered = kpiData.map(kpi => {
      const filter = kpiFilters[kpi.title];
      return {
        ...kpi,
        displayCount: kpi.count[filter],
        displayValue: kpi.value[filter]
      };
    });
    setFilteredKpiData(filtered);
  }, [kpiFilters, timeFrame]);
  // Handle outside clicks for dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.time-dropdown')) {
        setShowTimeFrameDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const recentActivity = getRecentActivity();
  const formatLastUpdated = () => {
    return lastUpdated.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };
  return <div className="space-y-6" data-id="element-599">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-600">
        <div data-id="element-601">
          <h1 className="text-2xl font-bold text-[#1A1F36]" data-id="element-602">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-603">
            Overview of your banking operations and transactions for{' '}
            {bankInfo.name}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3" data-id="element-604">
          <div className="relative time-dropdown" data-id="element-605">
            <button onClick={() => setShowTimeFrameDropdown(!showTimeFrameDropdown)} className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-606">
              {timeFrame === 'today' && 'Today'}
              {timeFrame === 'month' && 'This Month'}
              {timeFrame === 'year' && 'This Year'}
              <ChevronDownIcon size={16} className="ml-2" data-id="element-607" />
            </button>
            {showTimeFrameDropdown && <div className="absolute right-0 mt-1 w-40 bg-white rounded-md shadow-lg z-10 border border-gray-200" data-id="element-608">
                <button onClick={() => {
              setTimeFrame('today');
              setShowTimeFrameDropdown(false);
            }} className={`block w-full text-left px-4 py-2 text-sm ${timeFrame === 'today' ? 'bg-gray-100 text-[#F58220]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-609">
                  Today
                </button>
                <button onClick={() => {
              setTimeFrame('month');
              setShowTimeFrameDropdown(false);
            }} className={`block w-full text-left px-4 py-2 text-sm ${timeFrame === 'month' ? 'bg-gray-100 text-[#F58220]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-610">
                  This Month
                </button>
                <button onClick={() => {
              setTimeFrame('year');
              setShowTimeFrameDropdown(false);
            }} className={`block w-full text-left px-4 py-2 text-sm ${timeFrame === 'year' ? 'bg-gray-100 text-[#F58220]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-611">
                  This Year
                </button>
              </div>}
          </div>
          <button onClick={handleRefresh} className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-612">
            <RefreshCwIcon size={16} className="mr-2" data-id="element-613" />
            Refresh
          </button>
        </div>
      </div>
      <div className="flex items-center justify-between bg-blue-50 p-3 rounded-lg border border-blue-100" data-id="element-614">
        <div className="flex items-center" data-id="element-615">
          <InfoIcon size={18} className="text-blue-500 mr-2" data-id="element-616" />
          <span className="text-sm text-blue-800" data-id="element-617">
            Dashboard data last updated at {formatLastUpdated()}
          </span>
        </div>
        <div className="text-xs text-blue-600" data-id="element-618">
          {timeFrame === 'today' && "Showing today's data"}
          {timeFrame === 'month' && "Showing this month's data"}
          {timeFrame === 'year' && "Showing this year's data"}
          {` for ${bankInfo.name}`}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-619">
        {filteredKpiData.map((kpi, index) => <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow" data-id="element-620">
            <div className="flex justify-between items-start" data-id="element-621">
              <div className="flex items-center" data-id="element-622">
                <div className="rounded-full bg-gray-100 p-2 mr-3" data-id="element-623">
                  {kpi.icon}
                </div>
                <h3 className="text-sm font-medium text-gray-500" data-id="element-624">
                  {kpi.title}
                </h3>
              </div>
              <div className={`flex items-center text-xs font-medium ${kpi.trend === 'up' ? 'text-green-600' : 'text-red-600'}`} data-id="element-625">
                {kpi.trend === 'up' ? <ArrowUpIcon size={12} data-id="element-626" /> : <ArrowDownIcon size={12} data-id="element-627" />}
                <span className="ml-1" data-id="element-628">{kpi.percent}%</span>
              </div>
            </div>
            <div className="mt-4" data-id="element-629">
              <div className="text-3xl font-bold text-[#1A1F36]" data-id="element-630">
                {kpi.displayCount.toLocaleString()}
              </div>
              {kpi.displayValue > 0 && <div className="text-sm text-gray-600 mt-1" data-id="element-631">
                  ETB {kpi.displayValue.toLocaleString()}
                </div>}
            </div>
            <div className="mt-4 flex justify-between text-xs" data-id="element-632">
              <button className={`px-2 py-1 rounded-full ${kpiFilters[kpi.title] === 'all' ? 'bg-gray-200 text-gray-800' : 'text-gray-500 hover:bg-gray-100'}`} onClick={() => handleKpiFilterChange(kpi.title, 'all')} data-id="element-633">
                All
              </button>
              <button className={`px-2 py-1 rounded-full ${kpiFilters[kpi.title] === 'success' ? 'bg-green-100 text-green-800' : 'text-gray-500 hover:bg-gray-100'}`} onClick={() => handleKpiFilterChange(kpi.title, 'success')} data-id="element-634">
                Success
              </button>
              <button className={`px-2 py-1 rounded-full ${kpiFilters[kpi.title] === 'failed' ? 'bg-red-100 text-red-800' : 'text-gray-500 hover:bg-gray-100'}`} onClick={() => handleKpiFilterChange(kpi.title, 'failed')} data-id="element-635">
                Failed
              </button>
            </div>
          </div>)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-636">
        <div className="lg:col-span-2" data-id="element-637">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6" data-id="element-638">
            <div className="flex justify-between items-center mb-6" data-id="element-639">
              <h3 className="font-medium text-[#1A1F36]" data-id="element-640">Transaction Count</h3>
              <div className="flex items-center text-xs text-gray-500" data-id="element-641">
                <InfoIcon size={14} className="mr-1" data-id="element-642" />
                <span data-id="element-643">
                  {timeFrame === 'today' && 'Today'}
                  {timeFrame === 'month' && 'This Month'}
                  {timeFrame === 'year' && 'This Year'}
                </span>
              </div>
            </div>
            <div className="h-64" data-id="element-644">
              <ResponsiveContainer width="100%" height="100%" data-id="element-645">
                <AreaChart data={getTransactionData()} margin={{
                top: 10,
                right: 30,
                left: 0,
                bottom: 0
              }} data-id="element-646">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-647" />
                  <XAxis dataKey="name" tick={{
                  fontSize: 12
                }} data-id="element-648" />
                  <YAxis tick={{
                  fontSize: 12
                }} data-id="element-649" />
                  <Tooltip contentStyle={{
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }} labelStyle={{
                  fontWeight: 'bold',
                  marginBottom: '5px'
                }} data-id="element-650" />
                  <Legend wrapperStyle={{
                  paddingTop: '10px'
                }} data-id="element-651" />
                  <Area type="monotone" dataKey="p2p" stackId="1" stroke="#F58220" fill="#F58220" fillOpacity={0.6} name="P2P" data-id="element-652" />
                  <Area type="monotone" dataKey="p2m" stackId="1" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} name="P2M" data-id="element-653" />
                  <Area type="monotone" dataKey="qrc" stackId="1" stroke="#1A1F36" fill="#1A1F36" fillOpacity={0.6} name="QRC" data-id="element-654" />
                  <Area type="monotone" dataKey="rtp" stackId="1" stroke="#4C9AFF" fill="#4C9AFF" fillOpacity={0.6} name="RTP" data-id="element-655" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6" data-id="element-656">
          <div className="flex justify-between items-center mb-6" data-id="element-657">
            <h3 className="font-medium text-[#1A1F36]" data-id="element-658">
              Transaction Distribution
            </h3>
            <div className="flex items-center text-xs text-gray-500" data-id="element-659">
              <InfoIcon size={14} className="mr-1" data-id="element-660" />
              <span data-id="element-661">By Type</span>
            </div>
          </div>
          <div className="h-64 flex items-center justify-center" data-id="element-662">
            <ResponsiveContainer width="100%" height="100%" data-id="element-663">
              <PieChart data-id="element-664">
                <Pie data={transactionDistribution} cx="50%" cy="50%" labelLine={false} outerRadius={80} fill="#8884d8" dataKey="value" label={({
                name,
                percent
              }) => `${name} ${(percent * 100).toFixed(0)}%`} data-id="element-665">
                  {transactionDistribution.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-666" />)}
                </Pie>
                <Tooltip formatter={value => [`${value}%`, 'Percentage']} data-id="element-667" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-668">
        <div className="lg:col-span-2" data-id="element-669">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6" data-id="element-670">
            <div className="flex justify-between items-center mb-6" data-id="element-671">
              <h3 className="font-medium text-[#1A1F36]" data-id="element-672">Transaction Value</h3>
              <div className="flex items-center text-xs text-gray-500" data-id="element-673">
                <InfoIcon size={14} className="mr-1" data-id="element-674" />
                <span data-id="element-675">
                  {timeFrame === 'today' && 'Today'}
                  {timeFrame === 'month' && 'This Month'}
                  {timeFrame === 'year' && 'This Year'}
                </span>
              </div>
            </div>
            <div className="h-64" data-id="element-676">
              <ResponsiveContainer width="100%" height="100%" data-id="element-677">
                <BarChart data={getValueData()} margin={{
                top: 10,
                right: 30,
                left: 0,
                bottom: 0
              }} data-id="element-678">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-679" />
                  <XAxis dataKey="name" tick={{
                  fontSize: 12
                }} data-id="element-680" />
                  <YAxis tick={{
                  fontSize: 12
                }} data-id="element-681" />
                  <Tooltip formatter={value => [`ETB ${value.toLocaleString()}`, '']} contentStyle={{
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }} labelStyle={{
                  fontWeight: 'bold',
                  marginBottom: '5px'
                }} data-id="element-682" />
                  <Legend wrapperStyle={{
                  paddingTop: '10px'
                }} data-id="element-683" />
                  <Bar dataKey="p2p" name="P2P" fill="#F58220" radius={[4, 4, 0, 0]} data-id="element-684" />
                  <Bar dataKey="p2m" name="P2M" fill="#8884d8" radius={[4, 4, 0, 0]} data-id="element-685" />
                  <Bar dataKey="qrc" name="QRC" fill="#1A1F36" radius={[4, 4, 0, 0]} data-id="element-686" />
                  <Bar dataKey="rtp" name="RTP" fill="#4C9AFF" radius={[4, 4, 0, 0]} data-id="element-687" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6" data-id="element-688">
          <div className="flex justify-between items-center mb-4" data-id="element-689">
            <h3 className="font-medium text-[#1A1F36]" data-id="element-690">System Status</h3>
            <div className="text-xs text-gray-500" data-id="element-691">Live status</div>
          </div>
          <div className="space-y-4" data-id="element-692">
            {systemStatus.map(system => <div key={system.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-id="element-693">
                <span className="font-medium text-gray-700" data-id="element-694">{system.name}</span>
                <div className="flex items-center" data-id="element-695">
                  {system.status === 'operational' ? <>
                      <span className="h-2 w-2 bg-green-500 rounded-full mr-2" data-id="element-696"></span>
                      <span className="text-sm text-green-600" data-id="element-697">
                        Operational
                      </span>
                    </> : system.status === 'degraded' ? <>
                      <span className="h-2 w-2 bg-yellow-500 rounded-full mr-2" data-id="element-698"></span>
                      <span className="text-sm text-yellow-600" data-id="element-699">Degraded</span>
                    </> : <>
                      <span className="h-2 w-2 bg-red-500 rounded-full mr-2" data-id="element-700"></span>
                      <span className="text-sm text-red-600" data-id="element-701">Down</span>
                    </>}
                </div>
              </div>)}
          </div>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden" data-id="element-702">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center" data-id="element-703">
          <h3 className="font-medium text-[#1A1F36]" data-id="element-704">Recent Transactions</h3>
          <a href="#view-all" className="text-sm text-[#F58220] hover:text-[#e07619]" data-id="element-705">
            View All
          </a>
        </div>
        <div className="overflow-x-auto" data-id="element-706">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-707">
            <thead className="bg-gray-50" data-id="element-708">
              <tr data-id="element-709">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-710">
                  Date & Time
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-711">
                  Sender Bank
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-712">
                  Receiver Bank
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-713">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-714">
                  Txn Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-715">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-716">
              {recentActivity.map(activity => <tr key={activity.id} className={`hover:bg-gray-50 ${activity.status === 'Failed' ? 'bg-red-50' : ''}`} data-id="element-717">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-718">
                    {activity.dateTime}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-719">
                    {activity.sender}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-720">
                    {activity.receiver}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-721">
                    ETB {activity.amount.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-722">
                    {activity.txnType}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-723">
                    <div className="flex items-center" data-id="element-724">
                      {activity.status === 'Success' ? <CheckCircleIcon size={16} className="text-green-500 mr-2" data-id="element-725" /> : <AlertCircleIcon size={16} className="text-red-500 mr-2" data-id="element-726" />}
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${activity.status === 'Success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-727">
                        {activity.status}
                      </span>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}